import React, { useEffect, useState, useRef, useMemo, memo} from "react";
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Draggable from "react-draggable";
import {Column, Table} from 'react-virtualized';


const OutlinedTable = ({
  columns = [],
  rows = [],
  key="outlined-table",
  sorting=true,
  tableId=null,
  exportable=false,
  defaultSortBy=null,
  defaultSortByDirection="asc",
  exportOnly=false
}) => {

  
  const [sortBy, setSortBy] = useState(defaultSortBy);
  const [sortDirection, setSortDirection] = useState(defaultSortByDirection);

  getColumns = (options = {}) => {
    return columns.filter(x=>{
      if(options.export){
        return !x.disableExport
      }else {
        return !x.hidden
      }
    })
  }

  const renderHeaderRow = (options = {}) => {
    return (
      <thead>
        <tr>
          {getColumns(options).map(column=>{
            return (
              <OutlinedTableCellHeader
                key={`sg-outlined-table-${key}-${column.id}`}
                column={column}
                resizable={column.resizable}
                handleHeaderClick={handleHeaderClick}
                sortBy={sortBy}
                sortDirection={sortDirection}
                sorting={sorting}
                {...options}
              />
            )
          })}
        </tr>
      </thead>
    )
  }

  const handleHeaderClick = (column) => {
    if(sorting) {
      if(sortBy === column.id) {
        if(sortDirection === "asc") {
          setSortDirection(sortDirection === "asc" ? "desc" : "asc")
        }else {
          setSortBy(null)
          setSortDirection("asc")
        }
      }else {
        setSortBy(column.id)
        setSortDirection("asc")
      }
    }
  }

  


  getSortingColumn = (options = {}) => {
    console.log({
      columns: getColumns(options),
      sortBy
    })
    if(sortBy) {
      return getColumns(options).find(x=>x.id == sortBy)
    }
    return null
  }

  const getRows = () => {
    if(sortBy) {
      const column = getSortingColumn()
      if(!column) {
        return rows
      }
      console.log({
        column,
      })
      if(column.customSort) {
        return column.customSort(rows, column, sortDirection)
      }
      return rows.sort((a, b)=>{
        const aValue = getCellValue(a, column)
        const bValue = getCellValue(b, column)
        if(sortDirection == "asc") {
          return aValue > bValue ? 1 : -1
        }else {
          return aValue < bValue ? 1 : -1
        }
      })
    }
    return rows
  }

  const defaultColumns = useMemo(() => {
    return getColumns();
  }, [columns]);

  return (
    <>
    {exportOnly ? "" : (
      <table id={tableId} className="min-w-full sg-outlined-table">
        {renderHeaderRow({})}
        <tbody>
          {getRows().map(row=>
            <MemoOutlinedTableRow
              key={row.key ? row.key : row.id}
              row={row}
              columns={defaultColumns}
            />
          )}
        </tbody>
      </table>
    )}
    {exportable && (
      <table id={(tableId||"")+"-export"} className="hidden">
        {renderHeaderRow({
          export: true,
          sorting: false,
          resizable: false
        })}
          {getRows().map(row=>
            <MemoOutlinedTableRow
              key={row.key ? row.key : row.id}
              row={row}
              columns={defaultColumns}
              options={{
                export: true
              }}
            />
          )}
      </table>
    )}

    </>
  )
}

const OutlinedTableRow = ({
  row,
  columns,
  key="outlined-table",
  options={}
}) => {

  const rowRef = useRef();
  const [height, setHeight] = useState(null);
  const [ visible, setVisible ] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        } else {
          setVisible(false);
        }
      }
    );

    observer.observe(rowRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const element = rowRef.current;
    if (!element) return;

    const observer = new ResizeObserver(() => {
      // Only update height while visible
      if (visible) {
        setHeight(element.offsetHeight);
      }
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [visible]);

  
  const isVisible = () => {
    return visible || options.export
  }

  const renderCell = (row, column, options) => {
    const styles = isVisible() && height? {} : {
      height: height+"px",
    }
    return (
      <td
        style={{...column.cellStyle, ...styles}}
        className={`${column.className||""} ${column.cellClassName||""}`}
        key={`sg-outlined-table-${key}-${row.id}-${column.id||""}`}
      >
        {visible || options.export ? renderCellInner(row, column, options) : ""}
      </td>
    )
  }

  const renderCellInner = (row, column, options={}) => {
    if(options.export && column.renderCellExport) {
      return column.renderCellExport(row, column)
    }
    return column.renderCell ? column.renderCell(row, column, self) : (<span>{getCellValue(row, column)}</span>)
  }

  return (
    <tr ref={rowRef} key={`sg-outlined-table-${key}-${row.id}`} className={`sg-outlined-table-row-${visible ? "visible" : "hidden"}`}>
      {columns.map(column=>renderCell(row, column, options))}
    </tr>
  )
}

const MemoOutlinedTableRow = memo(
  OutlinedTableRow,
  (prev, next) => {
    return prev.row === next.row && prev.columns === next.columns;
  }
);


export default OutlinedTable

const OutlinedTableCellHeader = ({
  column,
  handleHeaderClick,
  sorting,
  sortBy,
  sortDirection,
  resizable
}) => {
  const headerRef = useRef(null);

  // null = auto size
  const [width, setWidth] = useState(column.width );

  const resizeColumn = ({ deltaX }) => {
    setWidth((currentWidth) => {
      const baseWidth =
        currentWidth === null
          ? headerRef.current?.getBoundingClientRect().width || 0
          : currentWidth;

      return Math.max(50, baseWidth + deltaX); // enforce minimum width
    });
  };

  return (
    <th
      ref={headerRef}
      onClick={() => handleHeaderClick(column)}
      className={`${column.className} ${column.headerClassName}`}
      style={{
        ...column.headerStyle,
        position: "relative",
        cursor: column.sorting === false ? "default" : "pointer",

        // auto until resized
        width: width === null ? undefined : `${width}px`,
        minWidth: width === null ? undefined : `${width}px`,
        maxWidth: width === null ? undefined : `${width}px`,

        whiteSpace: "nowrap",
        paddingRight: "30px",
        textOverflow: "ellipsis",
        overflow: "hidden"
      }}
    >
      {column.renderHeaderCell
        ? column.renderHeaderCell()
        : column.label}

      {sorting && column.sorting !== false && (
        <div
          style={{
            display: "inline-block",
            marginLeft: "5px",
            marginTop: "2px",
            marginRight: "5px",
            position: "absolute",
            right: "10px"
          }}
        >
          {sortBy === column.id ? (
            <ArrowUpwardIcon
              style={{
                transform: `rotate(${sortDirection === "asc" ? 0 : 180}deg)`
              }}
            />
          ) : (
            <SortIcon />
          )}
        </div>
      )}

      {resizable !== false && (
        <Draggable
          axis="x"
          position={{ x: 0 }}
          onDrag={(event, data) => {
            event.preventDefault();
            event.stopPropagation();

            resizeColumn({
              deltaX: data.deltaX
            });
          }}
        >
          <span className="DragHandleIcon absolute right-0 px-2">
            ⋮
          </span>
        </Draggable>
      )}
    </th>
  );
};

const getCellValue = (row, column) => {
  const valueGetter = column.valueGetter ? column.valueGetter : 
  ((row, column, self) => {
    return row[column.id]
  });
  const value =  valueGetter(row, column)
  return (
    value
  )
}